import { useQuery } from '@tanstack/react-query';

import {
  useHasFeePolicy,
  useIsChecklessPaymentsEnabled
} from 'reservations/account/providers/ConfigProvider';
import { getBookingEndpoint } from 'reservations/base/constants/endpoints';
import { MINUTE_IN_MILLISECONDS } from 'reservations/base/constants/time';
import { useQueryParams } from 'reservations/base/hooks/useQueryParams';
import { generateDummyBooking } from 'reservations/booking/helpers';
import { BookingQueryResponseSchema } from 'reservations/booking/types';
import { usePreviewModeContext } from 'reservations/preview/providers/PreviewModeProvider';
import { restClient } from 'utility/restClient';

const DEFAULT_QUERY_OPTIONS = {
  staleTime: MINUTE_IN_MILLISECONDS * 5,
  gcTime: MINUTE_IN_MILLISECONDS * 5,
  refetchInterval: MINUTE_IN_MILLISECONDS * 5
};

async function fetchBooking(endpoint: string) {
  const response = await restClient.get(endpoint);

  try {
    return BookingQueryResponseSchema.parse(response);
  } catch (err) {
    console.error(`fetchBooking: ${err}`);
    throw err;
  }
}

export const useBookingQueryV2 = (bookingId: string, hasVisitId: boolean) => {
  const { visitId } = useQueryParams();
  const endpoint = getBookingEndpoint(bookingId, '', { visitId });
  const isChecklessEnabled = useIsChecklessPaymentsEnabled();
  const { isPreview } = usePreviewModeContext();
  const hasFeePolicy = useHasFeePolicy();

  return useQuery({
    ...DEFAULT_QUERY_OPTIONS,
    queryKey: ['bentoboxReservations', 'booking', 'v2', bookingId, visitId],
    queryFn: () => {
      if (isPreview) {
        return Promise.resolve(
          generateDummyBooking(hasFeePolicy, isChecklessEnabled)
        );
      }

      return fetchBooking(endpoint);
    },
    enabled: hasVisitId,
    retry: false
  });
};
